// src/components/MapCard.js
import React from "react";
import { useNavigate } from "react-router-dom";
import "./MapCard.css";

const IS_DEV = process.env.NODE_ENV === "development";

function resolveUploadsUrl(src) {
  if (!src) return "";
  const s = String(src).trim();
  if (/^https?:\/\//i.test(s)) return s;
  if (s.startsWith("/")) {
    if (IS_DEV) return `https://beta.point-atlas.io${s}`;
    return s;
  }
  if (IS_DEV) return `https://beta.point-atlas.io/${s}`;
  return `/${s}`;
}

function capitalise(str) {
  if (!str) return "";
  return str.charAt(0).toUpperCase() + str.slice(1);
}

function isOfflineMap(map) {
  if (!map || !map.id) return false;
  return typeof map.id === "string" && (!/^\d+$/.test(map.id) || map.id.startsWith("offline-"));
}

function getDisplayName(map) {
  if (isOfflineMap(map) && map.meta) {
    const name = map.meta.name || "";
    const game = map.meta.game || "";
    return {
      main: name ? capitalise(name) : "",
      game: game ? capitalise(game) : null,
    };
  }
  if (
    !map.name ||
    map.name.trim() === "" ||
    map.name.trim().toLowerCase() === map.game_name?.trim().toLowerCase()
  ) {
    return { main: capitalise(map.game_name), game: null };
  }
  return { main: map.name, game: capitalise(map.game_name) };
}

function slugifyName(name) {
  const raw = name == null ? "" : String(name);
  const trimmed = raw.trim();
  const base = trimmed.length ? trimmed : "Map";
  const s = base
    .replace(/[\u0000-\u001f\u007f]+/g, " ")
    .replace(/\s+/g, " ")
    .trim()
    .replace(/\s/g, "-")
    .replace(/[^\w\-]+/g, "-")
    .replace(/\-+/g, "-")
    .replace(/^\-+|\-+$/g, "");
  return s || "Map";
}

function getPrettyUrl(map) {
  const mode = (map.mode || "").toLowerCase();
  const isTabletopMode =
    mode === "vtt" ||
    mode === "dnd" ||
    mode === "tabletop";

  const display = getDisplayName(map);
  const nameForSlug = map && map.name && String(map.name).trim() ? map.name : display.main;
  const slugName = slugifyName(nameForSlug);

  if (map && map.id != null) {
    const basePath = isTabletopMode ? "/vtt" : "/map";
    return `${basePath}/${encodeURIComponent(String(map.id))}/${encodeURIComponent(slugName)}`;
  }

  return "/map";
}

function MapCard({
  map,
  user,
  votes,
  userVote,
  onVote,
  isFavourited,
  favLoading,
  onFavourite,
  showFavouriteButton = false,
  showVotes = false,
  showEdit = false,
  onEdit,
  showDelete = false,
  onDelete,
  isHidden = false,
  children,
}) {
  const navigate = useNavigate();
  const offline = isOfflineMap(map);
  const display = getDisplayName(map);

  function getThumbUrl() {
    if (offline) {
      if (map.image instanceof Blob) {
        return URL.createObjectURL(map.image);
      }
      if (typeof map.image === "string" && map.image.startsWith("data:image")) {
        return map.image;
      }
      if (map.imageBase64) {
        return map.imageBase64;
      }
      return "/offline-placeholder.png";
    }

    const src =
      map.thumb_url ||
      map.thumbnail_url ||
      map.image_url ||
      map.default_map_url ||
      map.image;

    if (!src) return "/offline-placeholder.png";
    return resolveUploadsUrl(src);
  }

  function handleOpen() {
    if (offline) {
      navigate(`/offline-map/${map.id}`);
    } else {
      navigate(getPrettyUrl(map));
    }
  }

  function renderVotes() {
    if (offline || !showVotes) return null;
    return (
      <div className="map-votes">
        <button
          className={`map-vote-btn${userVote === 1 ? " active" : ""}`}
          onClick={onVote ? () => onVote(map.id, userVote === 1 ? 0 : 1) : undefined}
          title="Vote up"
          disabled={!onVote}
          tabIndex={-1}
        >👍</button>
        <span style={{ fontSize: 17 }}>
          {typeof votes === "number" ? votes : map.votes || 0}
        </span>
        <button
          className={`map-vote-btn${userVote === -1 ? " active" : ""}`}
          onClick={onVote ? () => onVote(map.id, userVote === -1 ? 0 : -1) : undefined}
          title="Vote down"
          disabled={!onVote}
          tabIndex={-1}
        >👎</button>
      </div>
    );
  }

  function renderOwner(map) {
    if (offline || !map.uploader_id || !map.uploader_username) return null;
    return (
      <div
        className="map-owner"
        onClick={e => {
          e.stopPropagation();
          navigate(`/user/id/${map.uploader_id}/maps`);
        }}
        style={{ cursor: "pointer" }}
      >
        by {map.uploader_username}
      </div>
    );
  }

  function renderFavouriteButton() {
    if (offline || !showFavouriteButton) return null;
    return (
      <button
        className={`map-fav-btn${isFavourited ? " favourited" : ""}`}
        onClick={e => {
          e.stopPropagation();
          onFavourite();
        }}
        disabled={favLoading}
        title={isFavourited ? "Remove from favourites" : "Add to favourites"}
      >
        <span style={{ fontSize: 21, marginRight: 6 }}>
          {isFavourited ? "★" : "☆"}
        </span>
        {isFavourited ? "Favourited" : "Add to Favourites"}
      </button>
    );
  }

  function renderEditDelete() {
    if (!showEdit && !showDelete) return null;

    if (offline) {
      return (
        <div className="map-card-actions" style={{
          display: "flex",
          flexDirection: "row",
          width: "100%",
          justifyContent: "center",
          gap: "10px",
          margin: "6px auto 2px auto",
          padding: "0 10px 0 10px"
        }}>
          {showEdit && (
            <button
              className="map-modal-button"
              style={{
                background: "#23252b",
                color: "#fff",
                borderRadius: "7px",
                fontWeight: 600,
                padding: "4px 10px",
                fontSize: "15px",
                border: "none",
                outline: "none",
                cursor: "pointer",
                width: "100%",
                maxWidth: 65,
                margin: 0
              }}
              onClick={e => {
                e.stopPropagation();
                onEdit(map);
              }}
            >
              Edit
            </button>
          )}
          {showDelete && (
            <button
              className="map-modal-button"
              style={{
                background: "#b33",
                color: "#fff",
                borderRadius: "7px",
                fontWeight: 600,
                padding: "4px 10px",
                fontSize: "15px",
                border: "none",
                outline: "none",
                cursor: "pointer",
                width: "100%",
                maxWidth: 65,
                margin: 0
              }}
              onClick={e => {
                e.stopPropagation();
                onDelete(map);
              }}
            >
              Delete
            </button>
          )}
        </div>
      );
    }

    return (
      <div className="map-card-actions" style={{
        display: "flex",
        width: "100%",
        justifyContent: "space-between",
        gap: "10px",
        margin: "0px auto 2px auto",
        padding: "0 14px 0 14px"
      }}>
        {showEdit && (
          <button
            className="map-modal-button"
            style={{
              background: "#23252b",
              color: "#fff",
              borderRadius: "7px",
              fontWeight: 600,
              padding: "5px 18px",
              fontSize: "15px",
              border: "none",
              outline: "none",
              cursor: "pointer",
              width: "100%",
              maxWidth: 88
            }}
            onClick={e => {
              e.stopPropagation();
              onEdit(map);
            }}
          >
            Edit
          </button>
        )}
        {showDelete && (
          <button
            className="map-modal-button"
            style={{
              background: "#b33",
              color: "#fff",
              borderRadius: "7px",
              fontWeight: 600,
              padding: "5px 18px",
              fontSize: "15px",
              border: "none",
              outline: "none",
              cursor: "pointer",
              width: "100%",
              maxWidth: 88
            }}
            onClick={e => {
              e.stopPropagation();
              onDelete(map);
            }}
          >
            Delete
          </button>
        )}
      </div>
    );
  }

  const imgHeight = offline ? 92 : 120;
  const cardMinHeight = offline ? 165 : 270;
  const namePadding = offline ? "6px 0 1px 0" : "12px 0 4px 0";
  const fontSize = offline ? "1em" : "1.12em";

  return (
    <div
      className={`map-thumbnail${isHidden ? " map-hidden" : ""}`}
      onClick={handleOpen}
      style={{
        opacity: isHidden ? 0.76 : 1,
        border: isHidden ? "2px dashed #ff7722" : undefined,
        boxShadow: isHidden
          ? "0 0 0 3px #ff882255, 0 8px 32px #d2550088"
          : undefined,
        position: "relative",
        minHeight: cardMinHeight,
        maxWidth: 245,
        marginBottom: 8,
        display: "flex",
        flexDirection: "column",
        justifyContent: "flex-start"
      }}
    >
      {isHidden && (
        <div
          className="hidden-banner"
          style={{
            position: "absolute",
            top: 8,
            left: 8,
            background: "linear-gradient(90deg, #d25500ee, #a22e)",
            color: "#fff",
            padding: "4px 13px 4px 10px",
            borderRadius: 12,
            fontWeight: 700,
            fontSize: 15,
            display: "flex",
            alignItems: "center",
            zIndex: 1,
            letterSpacing: "0.5px",
            boxShadow: "0 2px 6px #000a"
          }}
        >
          <span role="img" aria-label="hidden" style={{ marginRight: 7, fontSize: 19 }}>
            🔒
          </span>
          Hidden
        </div>
      )}

      <img
        src={getThumbUrl()}
        alt={display.main}
        className="map-thumb-img"
        loading="lazy"
        decoding="async"
        style={{
          objectFit: "cover",
          background: "#222",
          width: "100%",
          height: imgHeight,
          borderTopLeftRadius: 16,
          borderTopRightRadius: 16,
          borderBottomLeftRadius: 0,
          borderBottomRightRadius: 0,
        }}
      />
      <div className="map-name" style={{
        fontWeight: 700,
        fontSize,
        padding: namePadding,
        whiteSpace: "nowrap",
        overflow: "hidden",
        textOverflow: "ellipsis"
      }}>
        {display.main}
        {display.game && (
          <span style={{
            color: "#aaa",
            fontSize: offline ? "0.96em" : "0.98em",
            fontWeight: "normal",
            marginLeft: 7
          }}>
            ({display.game})
          </span>
        )}
      </div>
      {renderVotes()}
      {renderOwner(map)}
      {renderFavouriteButton()}
      {renderEditDelete()}
      {children}
      {offline && (
        <div style={{
          position: "absolute",
          top: 10,
          left: "50%",
          transform: "translateX(-50%)",
          background: "#444b",
          color: "#ffe892",
          fontWeight: 700,
          fontSize: 13,
          padding: "2.5px 11px 2.5px 10px",
          borderRadius: 8,
          pointerEvents: "none",
          userSelect: "none",
        }}>
          Local
        </div>
      )}
    </div>
  );
}

export default MapCard;
